/**
 * FindMoreFlow - Chat-style flow for finding more results
 * Allows users to request additional matching leads via conversational interface
 */
import React, { useState, useEffect, useRef } from 'react';
import { Check, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { useSettingsModal } from '../../contexts/SettingsModalContext';
import { parseError } from '../../utils/errorUtils';
import { CreditPill } from '../ui';
import { calculateLeadCost } from '../../constants/credits';
import { AssistantMessage, UserMessage } from './shared';

interface FindMoreMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface FindMoreFlowProps {
  tableId: string;
  currentCount: number;
  initialCount?: number; // Pre-populated count from backend action parameters
  triggeredFromChat?: boolean; // When true, skip showing initial "Find more" user message (user already typed something)
  onFindMoreStarted?: (count: number) => void;
  onAddMessages?: (messages: FindMoreMessage[]) => void;
  autoFindMoreStatus?: {
    enabled?: boolean;
    status?: string;
    currentAttempt?: number;
    maxAttempts?: number;
  };
}

type SetupStep = 'intro' | 'finding' | 'done' | 'completed' | 'error';

interface ErrorInfo {
  message: string;
  details?: string;
}

const COUNT_OPTIONS = [10, 25, 50];

export const FindMoreFlow: React.FC<FindMoreFlowProps> = ({
  tableId,
  currentCount,
  initialCount,
  triggeredFromChat = false,
  onFindMoreStarted,
  onAddMessages,
  autoFindMoreStatus,
}) => {
  const { showToast } = useToast();
  const { openSettings } = useSettingsModal();

  // Check if auto find-more is in progress
  const isAutoInProgress = autoFindMoreStatus?.status === 'in_progress';
  const [step, setStep] = useState<SetupStep>('intro');
  // If initialCount is a standard option, pre-select it; otherwise use custom input
  const isStandardOption = initialCount && COUNT_OPTIONS.includes(initialCount);
  const [selectedCount, setSelectedCount] = useState(isStandardOption ? initialCount : 25);
  // Pre-fill custom count if initialCount is not a standard option
  const [customCount, setCustomCount] = useState(
    initialCount && !isStandardOption ? initialCount.toString() : ''
  );
  // Track if user wants to see full options instead of simplified pre-filled view
  const [showFullOptions, setShowFullOptions] = useState(false);
  const [submittedCount, setSubmittedCount] = useState(0);
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const [displayedIntroText, setDisplayedIntroText] = useState('');
  const [introComplete, setIntroComplete] = useState(false);
  const [round, setRound] = useState(1);
  const [completedRounds, setCompletedRounds] = useState<Array<{ count: number; roundNum: number }>>([]);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Clamp count to valid range (1-500) for display consistency with getFinalCount()
  const clampCount = (count: number) => Math.min(Math.max(count, 1), 500);

  // Customize intro text based on context
  const getIntroText = () => {
    if (round > 1) {
      return `Sure! How many more leads would you like me to find this time?`;
    }
    if (triggeredFromChat && initialCount) {
      const displayCount = clampCount(initialCount);
      return `Got it! I'll find ${displayCount} more leads for your dataset. You currently have ${currentCount} results. Ready to start?`;
    }
    return `I'd be happy to find more leads for your dataset! You currently have ${currentCount} results. How many more would you like me to find?`;
  };
  const introText = getIntroText();

  // Typewriter effect using requestAnimationFrame for smoother performance
  useEffect(() => {
    if (step === 'intro') {
      setDisplayedIntroText('');
      setIntroComplete(false);
      let index = 0;
      let lastTime = 0;
      const charDelay = 18; // ms per character
      let animationId: number;

      const animate = (currentTime: number) => {
        if (!lastTime) lastTime = currentTime;
        const elapsed = currentTime - lastTime;

        if (elapsed >= charDelay) {
          if (index < introText.length) {
            // Update multiple characters at once if we've fallen behind
            const charsToAdd = Math.min(Math.floor(elapsed / charDelay), introText.length - index);
            index += charsToAdd;
            setDisplayedIntroText(introText.slice(0, index));
            lastTime = currentTime;
          } else {
            setIntroComplete(true);
            return;
          }
        }

        animationId = requestAnimationFrame(animate);
      };

      animationId = requestAnimationFrame(animate);

      return () => {
        if (animationId) cancelAnimationFrame(animationId);
      };
    }
  }, [step, introText, round]);

  useEffect(() => {
    const timer = setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(timer);
  }, [step, displayedIntroText]);

  const getFinalCount = () => {
    if (customCount.trim()) {
      const parsed = parseInt(customCount, 10);
      return isNaN(parsed) ? selectedCount : clampCount(parsed);
    }
    return selectedCount;
  };

  const handleFindMore = async () => {
    const count = getFinalCount();
    setSubmittedCount(count);
    setStep('finding');
    setErrorInfo(null);

    try {
      await apiClient.findMoreResults(tableId, count);

      if (onFindMoreStarted) {
        onFindMoreStarted(count);
      }

      // Save flow messages to chat history
      if (onAddMessages) {
        const now = new Date();
        const messages: FindMoreMessage[] = [];

        // Only add the initial "Find more" message if NOT triggered from chat
        // (when triggered from chat, the user's message is already in the chat history)
        if (!triggeredFromChat) {
          messages.push({ id: `findmore-user-${now.getTime()}`, role: 'user', content: 'Find more', timestamp: now });
        }

        messages.push(
          { id: `findmore-assistant-intro-${now.getTime()}`, role: 'assistant', content: introText, timestamp: now },
          { id: `findmore-user-count-${now.getTime()}`, role: 'user', content: `${count} leads`, timestamp: now },
          { id: `findmore-assistant-success-${now.getTime()}`, role: 'assistant', content: `I've successfully started finding ${count} more leads for you. New results will appear in your table as they're found.`, timestamp: now }
        );

        onAddMessages(messages);
      }

      setStep('done');
      showToast(`Finding ${count} more leads...`, 'success');
    } catch (error: any) {
      console.error('Failed to find more leads:', error);

      const err = parseError(error);
      setErrorInfo({ message: err.message, details: err.details });

      if (err.shouldReturnToIntro) {
        showToast(err.message, {
          type: 'error',
          duration: 6000,
          action: { label: 'Upgrade', onClick: () => openSettings('billing') }
        });
        setStep('intro');
        setSelectedCount(10);
        setCustomCount('');
      } else if (err.requiresUpgrade) {
        showToast(err.message, {
          type: 'error',
          duration: 8000,
          action: { label: 'Upgrade', onClick: () => openSettings('billing') }
        });
        setStep('error');
      } else {
        showToast(err.message, 'error', 5000);
        setStep('error');
      }
    }
  };

  const handleRetry = () => {
    setStep('intro');
    setErrorInfo(null);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-shrink-0 space-y-4">
        {/* Only show initial "Find more" message if NOT triggered from chat (user already typed something) */}
        {!triggeredFromChat && <UserMessage>Find more</UserMessage>}

        {completedRounds.map((completedRound) => (
          <React.Fragment key={`completed-${completedRound.roundNum}`}>
            {completedRound.roundNum === 1 ? (
              <AssistantMessage>
                I'd be happy to find more leads for your dataset! You currently have {currentCount} results. How many more would you like me to find?
              </AssistantMessage>
            ) : (
              <>
                <UserMessage>Find more</UserMessage>
                <AssistantMessage>
                  Sure! How many more leads would you like me to find this time?
                </AssistantMessage>
              </>
            )}
            <UserMessage>{completedRound.count} leads</UserMessage>
            <AssistantMessage>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-gray-600" />
                </div>
                <div>
                  <span>I've successfully started finding {completedRound.count} more leads for you.</span>
                  <div className="text-[13px] text-gray-500 mt-0.5">
                    New results will appear in your table as they're found.
                  </div>
                </div>
              </div>
            </AssistantMessage>
          </React.Fragment>
        ))}

        {round > 1 && step === 'intro' && (
          <UserMessage>Find more</UserMessage>
        )}

        {(step === 'intro' || step === 'finding' || step === 'done' || step === 'error') && (
          <AssistantMessage>
            {step === 'intro' ? (
              <>
                {displayedIntroText}
                {!introComplete && <span className="inline-block w-0.5 h-4 bg-gray-400 ml-0.5 animate-pulse" />}
              </>
            ) : (
              introText
            )}
          </AssistantMessage>
        )}

        {step === 'intro' && introComplete && (
          <div className="ml-10 animate-message-in" style={{ animationDelay: '100ms' }}>
            <div className="flex flex-col gap-3">
              {/* Show message if auto find-more is in progress */}
              {isAutoInProgress ? (
                <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
                  <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                  <span className="text-[13px] text-gray-600">
                    Searching for more results...
                  </span>
                </div>
              ) : triggeredFromChat && initialCount && !showFullOptions ? (
                /* When count is pre-filled from chat, show simplified confirmation */
                <>
                  <button
                    type="button"
                    onClick={handleFindMore}
                    className="w-full px-4 py-2.5 text-[13px] font-medium rounded-xl transition-colors active:scale-[0.98] bg-gray-900 text-white hover:bg-gray-800 flex items-center justify-center gap-2"
                  >
                    Find {clampCount(initialCount!)} more leads
                    <CreditPill cost={calculateLeadCost(clampCount(initialCount!))} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowFullOptions(true);
                      setCustomCount('');
                      setSelectedCount(25);
                    }}
                    className="text-[12px] text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Choose a different amount
                  </button>
                </>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2">
                    {COUNT_OPTIONS.map((count) => (
                      <button
                        type="button"
                        key={count}
                        onClick={() => {
                          setSelectedCount(count);
                          setCustomCount('');
                        }}
                        className={`px-4 py-2 text-[13px] font-medium rounded-lg transition-all active:scale-[0.98] ${
                          selectedCount === count && !customCount.trim()
                            ? 'bg-gray-900 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {count} leads
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-[13px] text-gray-500">Or enter a custom number:</span>
                    <input
                      ref={inputRef}
                      type="number"
                      value={customCount}
                      onChange={(e) => setCustomCount(e.target.value)}
                      placeholder="e.g., 75"
                      min="1"
                      max="500"
                      className="w-24 px-3 py-2 text-[14px] border border-gray-200 rounded-lg outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-100 transition-all"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleFindMore();
                        }
                      }}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleFindMore}
                    className="w-full px-4 py-2.5 text-[13px] font-medium rounded-xl transition-colors active:scale-[0.98] bg-gray-900 text-white hover:bg-gray-800 flex items-center justify-center gap-2"
                  >
                    Find {getFinalCount()} more leads
                    <CreditPill cost={calculateLeadCost(getFinalCount())} />
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {step !== 'intro' && submittedCount > 0 && (
          <UserMessage>{submittedCount} leads</UserMessage>
        )}

        {step === 'finding' && (
          <AssistantMessage>
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
              <span>Searching for {submittedCount} more leads...</span>
            </div>
          </AssistantMessage>
        )}

        {(step === 'done' || step === 'completed') && (
          <AssistantMessage>
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Check className="w-3 h-3 text-gray-600" />
              </div>
              <div>
                <span>I've successfully started finding {submittedCount} more leads for you.</span>
                <div className="text-[13px] text-gray-500 mt-0.5">
                  New results will appear in your table as they're found. Let me know if you'd like to find more!
                </div>
              </div>
            </div>

            {step === 'done' && (
              <div className="flex mt-3">
                <button
                  type="button"
                  onClick={() => {
                    setCompletedRounds(prev => [...prev, { count: submittedCount, roundNum: round }]);
                    setSelectedCount(25);
                    setCustomCount('');
                    setSubmittedCount(0);
                    setStep('intro');
                    setRound(prev => prev + 1);
                  }}
                  className="px-4 py-2 text-[13px] font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition-all active:scale-[0.98]"
                >
                  Find more leads
                </button>
              </div>
            )}
          </AssistantMessage>
        )}

        {step === 'error' && errorInfo && (
          <>
            <AssistantMessage>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <AlertCircle className="w-3 h-3 text-gray-500" />
                </div>
                <div>
                  <span>{errorInfo.message}</span>
                  {errorInfo.details && (
                    <div className="text-[13px] text-gray-500 mt-0.5">
                      {errorInfo.details}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-2 mt-3">
                {parseError({ message: errorInfo.message }).requiresUpgrade ? (
                  <button
                    type="button"
                    onClick={() => openSettings('billing')}
                    className="flex items-center gap-1.5 px-4 py-2 text-[13px] font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-all active:scale-[0.98]"
                  >
                    Upgrade Plan
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleRetry}
                    className="flex items-center gap-1.5 px-4 py-2 text-[13px] font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-all active:scale-[0.98]"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Try Again
                  </button>
                )}
              </div>
            </AssistantMessage>
          </>
        )}

        <div ref={bottomRef} />
      </div>

      <div className="flex-grow min-h-[20px]" />

      <style>{`
        @keyframes messageIn {
          0% {
            opacity: 0;
            transform: translateY(12px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-message-in {
          animation: messageIn 400ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }
      `}</style>
    </div>
  );
};

export default FindMoreFlow;
